import { fetchWithDeadline } from "./apiDeadline";

export type NavLink = {
  label: string;
  href: string;
  sub?: boolean;
  /** Open as a full page (e.g. Swagger ``/docs``) instead of an SPA route. */
  external?: boolean;
};
export type NavSection = {
  title: string;
  links: NavLink[];
};
export type SessionUser = {
  username: string;
  role: string;
  /** When set, user is scoped to this project (templates/devices filtered). */
  project_id?: string;
  /** Local debug demo account (Admin); may open FlowChat without SuperAdmin. */
  demo_account?: boolean;
};
export type SessionPayload = {
  authenticated: boolean;
  csrfToken: string;
  user: SessionUser | null;
  navigation: NavSection[];
  messages: Array<{ category: string; message: string }>;
  modbusPerm?: unknown;
  alarmRulesPerm?: { view?: boolean; add?: boolean; edit?: boolean; delete?: boolean };
  dashboardPerm?: { remote_control?: boolean };
  /** IANA timezone from System settings (header clock / display). */
  system_timezone?: string;
};
export type PageDataset = {
  name: string;
  label: string;
  rows: unknown[];
  columns?: string[];
  count?: number;
};
export type PageData = {
  route: string;
  title: string;
  description: string;
  datasets: PageDataset[];
  actions: Array<{ label: string; href: string; kind?: string }>;
  meta: Record<string, unknown>;
};
export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}
/** User-facing copy for login-page “server unreachable” banners (not raw HTTP text). */
export type ConnectionBanner = {
  title: string;
  description: string;
  /** Shown only in development — optional technical context. */
  technicalHint?: string;
};
export type SessionBootstrapResult =
  | { ok: true; session: SessionPayload }
  | { ok: false; issue: ConnectionBanner };
/** Single message for inline login form errors (after sign-in attempt). */
export function connectionBannerToFormMessage(issue: ConnectionBanner): string {
  const main = [issue.title, issue.description].join(" ").replace(/\s+/g, " ").trim();
  return issue.technicalHint ? `${main} ${issue.technicalHint}`.trim() : main;
}
export function connectionBannerFromError(err: unknown): ConnectionBanner {
  const showTechnical = import.meta.env.DEV;
  if (err instanceof ApiError) {
    const code = err.status;
    if (code === 502 || code === 503 || code === 504) {
      return {
        title: "Unable to reach the application server",
        description:
          "The RMS service is not responding. It may still be starting, temporarily overloaded, or not running.",
        technicalHint: showTechnical ? `Bad gateway or upstream timeout (HTTP ${code}).` : undefined,
      };
    }
    if (code === 404) {
      return {
        title: "Service endpoint not available",
        description:
          "The console could not load session information from the server. Confirm you are using the correct address and that the RMS web service is deployed.",
        technicalHint: showTechnical ? err.message : undefined,
      };
    }
    if (code >= 500) {
      return {
        title: "Server error",
        description:
          "The RMS server reported a problem while checking your session. Please try again shortly.",
        technicalHint: showTechnical ? err.message : undefined,
      };
    }
    if (code === 401 || code === 403) {
      return {
        title: "Session check was rejected",
        description: err.message || "The server refused the session request.",
        technicalHint: showTechnical ? `HTTP ${code}` : undefined,
      };
    }
    return {
      title: "Unexpected response from server",
      description: "We could not complete the connection check. Please try again.",
      technicalHint: showTechnical ? `${err.message} (HTTP ${code})` : undefined,
    };
  }
  const raw = err instanceof Error ? `${err.message || ""}`.trim() : "";
  if (err instanceof Error && (err.name === "AbortError" || /timed out|timeout/i.test(raw))) {
    return {
      title: "The server is taking too long to respond",
      description: "The session check timed out. Your sign-in cookie is still in the browser. Retry when the network is ready.",
      technicalHint: showTechnical ? raw : undefined,
    };
  }
  const isNetworkFail =
    err instanceof TypeError ||
    (err instanceof Error &&
      (err.name === "TypeError" ||
        /\bfetch\b/i.test(raw) ||
        /networkerror|failed to fetch|load failed|network request failed/i.test(raw)));
  if (isNetworkFail) {
    return {
      title: "No connection to the server",
      description:
        "Your browser could not reach the RMS service. Check your network, VPN, or firewall. If you run the console locally, ensure the API process is started.",
      technicalHint: showTechnical
        ? [raw, "Dev: start `python web_app.py` in `backend/`; Vite must proxy `/api` to the same host you use in the browser (localhost vs 127.0.0.1)."]
            .filter(Boolean)
            .join(" ")
        : undefined,
    };
  }
  return {
    title: "Unable to connect",
    description: raw || "Something prevented the application from reaching the server.",
    technicalHint: undefined,
  };
}
export function userFacingLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 502 || err.status === 503 || err.status === 504) {
      return "The server is unavailable. Confirm the RMS service is running, then try again.";
    }
    if (err.status === 401) {
      return err.message || "Invalid username or password.";
    }
    if (err.status >= 500) {
      return "The server could not complete sign-in. Please try again in a moment.";
    }
    return err.message || "Sign-in failed.";
  }
  if (err instanceof Error) {
    const raw = err.message || "Sign-in failed.";
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
      return "Could not reach the server. Check your connection and that the RMS service is running.";
    }
    return raw;
  }
  return "Sign-in failed.";
}
const envBackend = (import.meta.env.VITE_BACKEND_URL || "").replace(/\/+$/, "");
const backendKey = import.meta.env.VITE_BACKEND_KEY || "";
const backendSecret = import.meta.env.VITE_BACKEND_SECRET || "";
const inFlightGet = new Map<string, Promise<unknown>>();
/**
 * API / asset URL prefix (no trailing slash). Empty string means same origin as the SPA — required
 * for session cookies on co-hosted deployments.
 *
 * - Dev: always "" (Vite proxies `/api`).
 * - Prod: uses `VITE_BACKEND_URL` only when it points at a **different** origin than the page;
 *   if the env URL matches `window.location.origin` (same host/scheme), returns "" so cookies and
 *   CSRF stay on one site. A mismatched port in env while the page uses another origin is a
 *   common cause of "logout on certain pages" when those pages use more direct fetches.
 */
export function spaAssetBase(): string {
  if (import.meta.env.DEV) return "";
  if (!envBackend) return "";
  if (typeof window === "undefined") return envBackend;
  try {
    if (new URL(envBackend).origin === window.location.origin) return "";
  } catch {
    /* ignore invalid env */
  }
  return envBackend;
}
/**
 * Page meta sometimes carries ``request.url_for(...)`` as a full ``http(s)://`` URL. Use it as-is.
 * Relative paths combine with ``spaAssetBase()`` (same-origin or ``VITE_BACKEND_URL``).
 */
export function resolveSpaConfiguredApiUrl(metaUrl: unknown, fallbackPath: string): string {
  const fb = fallbackPath.startsWith("/") ? fallbackPath : `/${fallbackPath}`;
  const raw = String(metaUrl ?? "").trim();
  const pick = raw || fb;
  if (/^https?:\/\//i.test(pick)) {
    try {
      return new URL(pick).href;
    } catch {
      /* fall through */
    }
  }
  const path = pick.startsWith("/") ? pick : `/${pick}`;
  return `${spaAssetBase()}${path}`;
}
function backendRequestUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base = spaAssetBase();
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
}
export function backendHeaders(extra?: HeadersInit): HeadersInit {
  return {
    ...(extra || {}),
    ...(backendKey ? { "X-KLT-API-Key": backendKey } : {}),
    ...(backendSecret ? { "X-KLT-API-Secret": backendSecret } : {})
  };
}
async function parseResponse<T>(response: Response): Promise<T> {
  const redirectOk =
    response.status === 301 ||
    response.status === 302 ||
    response.status === 303 ||
    response.status === 307 ||
    response.status === 308;
  if (redirectOk) {
    return { ok: true } as T;
  }
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed with ${response.status}`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}
export function inFlightGetCount(): number {
  return inFlightGet.size;
}
export async function apiGet<T>(url: string): Promise<T> {
  const requestUrl = backendRequestUrl(url);
  const existing = inFlightGet.get(requestUrl);
  if (existing) return existing as Promise<T>;
  const request = fetchWithDeadline(requestUrl, {
    credentials: "include",
    cache: "no-store",
    headers: backendHeaders({ Accept: "application/json" })
  })
    .then((response) => parseResponse<T>(response))
    .finally(() => {
      inFlightGet.delete(requestUrl);
    });
  inFlightGet.set(requestUrl, request);
  return request;
}
export async function apiFetchBlob(url: string): Promise<Blob> {
  const response = await fetch(backendRequestUrl(url), {
    credentials: "include",
    cache: "no-store",
    headers: backendHeaders({ Accept: "*/*" })
  });
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(text || `Request failed with ${response.status}`, response.status, text);
  }
  return response.blob();
}
export async function apiPost<T>(
  url: string,
  body: unknown,
  csrfToken?: string
): Promise<T> {
  const response = await fetchWithDeadline(backendRequestUrl(url), {
    method: "POST",
    credentials: "include",
    headers: backendHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
    }),
    body: JSON.stringify(body)
  });
  return parseResponse<T>(response);
}
/**
 * Legacy HTML form endpoints return 303/302 redirects on success. ``fetch`` is called with
 * ``redirect: "manual"``, so ``response.ok`` is false even when the server saved successfully.
 */
export function isFormPostSuccess(response: Response): boolean {
  if (response.type === "opaqueredirect") return true;
  const s = response.status;
  return (s >= 200 && s < 400) || s === 301 || s === 302 || s === 303 || s === 307 || s === 308;
}
/** Legacy form posts with ``X-KLT-React-Form`` return JSON ``{ ok, error, messages }`` instead of a redirect. */
export async function readFormPostResult(
  response: Response
): Promise<{ ok: boolean; error: string; messages: Array<{ category: string; message: string }> }> {
  if (!isFormPostSuccess(response)) {
    return { ok: false, error: `Request failed with ${response.status}`, messages: [] };
  }
  if (response.type === "opaqueredirect") {
    return { ok: true, error: "", messages: [] };
  }
  const ct = (response.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    return { ok: true, error: "", messages: [] };
  }
  try {
    const body = (await response.json()) as {
      ok?: boolean;
      error?: string;
      messages?: Array<{ category: string; message: string }>;
    };
    const messages = Array.isArray(body.messages) ? body.messages : [];
    if (body && body.ok === false) {
      const err =
        String(body.error || "").trim() ||
        messages
          .filter((m) => m?.category === "error" || m?.category === "danger")
          .map((m) => String(m.message || "").trim())
          .filter(Boolean)
          .join("; ") ||
        "Request failed.";
      return { ok: false, error: err, messages };
    }
    return { ok: true, error: "", messages };
  } catch {
    return { ok: true, error: "", messages: [] };
  }
}
export async function apiPostForm(url: string, form: FormData): Promise<Response> {
  return fetchWithDeadline(backendRequestUrl(url), {
    method: "POST",
    credentials: "include",
    redirect: "manual",
    headers: backendHeaders({ "X-KLT-React-Form": "1", Accept: "application/json" }),
    body: form
  });
}
export async function apiPostFormJson<T = { ok?: boolean; error?: string; redirect?: string }>(url: string, form: FormData): Promise<T> {
  const response = await apiPostForm(url, form);
  return parseResponse<T>(response);
}
